import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  QueryConstraint
} from 'firebase/firestore';
import { db } from './config';
import { User, Customer, Order, Product, Category, Table, DashboardMetrics } from '../types';

export class FirestoreService {
  // Users collection
  static async getUser(userId: string): Promise<User | null> {
    try {
      console.log('🔍 FirestoreService: Getting user with ID:', userId);
      const userDoc = await getDoc(doc(db, 'users', userId));
      console.log('📊 FirestoreService: User document exists:', userDoc.exists());
      console.log('📊 FirestoreService: User document ID:', userDoc.id);
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        console.log('✅ FirestoreService: User data found:');
        console.log('📊 FirestoreService: User data:', JSON.stringify(userData, null, 2));
        return userData;
      } else {
        console.log('⚠️ FirestoreService: User document not found for ID:', userId);
        return null;
      }
    } catch (error) {
      console.error('❌ FirestoreService: Error getting user:', error);
      console.error('❌ FirestoreService: Error details:', {
        userId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      return null;
    }
  }

  static async updateUser(userId: string, updates: Partial<User>): Promise<boolean> {
    try {
      await updateDoc(doc(db, 'users', userId), updates);
      return true;
    } catch (error) {
      console.error('Error updating user:', error);
      return false;
    }
  }

  // Customers collection
  static async getCustomers(): Promise<Customer[]> {
    try {
      console.log('🔍 FirestoreService: Getting customers from Firestore...');
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      console.log('📊 FirestoreService: Customers snapshot size:', customersSnapshot.size);
      console.log('📊 FirestoreService: Customers snapshot empty:', customersSnapshot.empty);
      
      const customers = customersSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📊 FirestoreService: Customer document:', doc.id, data);
        return {
          ...data,
          id: doc.id
        } as Customer;
      });
      
      console.log('✅ FirestoreService: Retrieved customers:', customers);
      return customers;
    } catch (error) {
      console.error('❌ FirestoreService: Error getting customers:', error);
      return [];
    }
  }

  static async getCustomer(customerId: string): Promise<Customer | null> {
    try {
      console.log('🔍 FirestoreService: Getting customer with ID:', customerId);
      const customerDoc = await getDoc(doc(db, 'customers', customerId));
      console.log('📊 FirestoreService: Customer document exists:', customerDoc.exists());
      console.log('📊 FirestoreService: Customer document ID:', customerDoc.id);
      
      if (customerDoc.exists()) {
        const customerData = {
          ...customerDoc.data(),
          id: customerDoc.id
        } as Customer;
        console.log('✅ FirestoreService: Customer data found:');
        console.log('📊 FirestoreService: Customer data:', JSON.stringify(customerData, null, 2));
        return customerData;
      } else {
        console.log('⚠️ FirestoreService: Customer document not found for ID:', customerId);
        return null;
      }
    } catch (error) {
      console.error('❌ FirestoreService: Error getting customer:', error);
      console.error('❌ FirestoreService: Error details:', {
        customerId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      return null;
    }
  }

  static async addCustomer(customer: Omit<Customer, 'id'>): Promise<string | null> {
    try {
      const docRef = await addDoc(collection(db, 'customers'), customer);
      return docRef.id;
    } catch (error) {
      console.error('Error adding customer:', error);
      return null;
    }
  }

  static async updateCustomer(customerId: string, updates: Partial<Customer>): Promise<boolean> {
    try {
      await updateDoc(doc(db, 'customers', customerId), updates);
      return true;
    } catch (error) {
      console.error('Error updating customer:', error);
      return false;
    }
  }

  static async deleteCustomer(customerId: string): Promise<boolean> {
    try {
      await deleteDoc(doc(db, 'customers', customerId));
      return true;
    } catch (error) {
      console.error('Error deleting customer:', error);
      return false;
    }
  }

  // Products collection
  static async getProducts(): Promise<Product[]> {
    try {
      const productsSnapshot = await getDocs(collection(db, 'products'));
      return productsSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Product));
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  static async getProduct(productId: string): Promise<Product | null> {
    try {
      const productDoc = await getDoc(doc(db, 'products', productId));
      return productDoc.exists() ? ({
        ...productDoc.data(),
        id: productDoc.id
      } as Product) : null;
    } catch (error) {
      console.error('Error getting product:', error);
      return null;
    }
  }

  static async addProduct(product: Omit<Product, 'id'>): Promise<string | null> {
    try {
      const docRef = await addDoc(collection(db, 'products'), product);
      return docRef.id;
    } catch (error) {
      console.error('Error adding product:', error);
      return null;
    }
  }

  static async updateProduct(productId: string, updates: Partial<Product>): Promise<boolean> {
    try {
      await updateDoc(doc(db, 'products', productId), updates);
      return true;
    } catch (error) {
      console.error('Error updating product:', error);
      return false;
    }
  }

  static async deleteProduct(productId: string): Promise<boolean> {
    try {
      await deleteDoc(doc(db, 'products', productId));
      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      return false;
    }
  }

  // Categories collection
  static async getCategories(): Promise<Category[]> {
    try {
      const categoriesSnapshot = await getDocs(collection(db, 'categories'));
      return categoriesSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Category));
    } catch (error) {
      console.error('Error getting categories:', error);
      return [];
    }
  }

  static async addCategory(category: Omit<Category, 'id'>): Promise<string | null> {
    try {
      const docRef = await addDoc(collection(db, 'categories'), category);
      return docRef.id;
    } catch (error) {
      console.error('Error adding category:', error);
      return null;
    }
  }

  static async updateCategory(categoryId: string, updates: Partial<Category>): Promise<boolean> {
    try {
      await updateDoc(doc(db, 'categories', categoryId), updates);
      return true;
    } catch (error) {
      console.error('Error updating category:', error);
      return false;
    }
  }

  static async deleteCategory(categoryId: string): Promise<boolean> {
    try {
      await deleteDoc(doc(db, 'categories', categoryId));
      return true;
    } catch (error) {
      console.error('Error deleting category:', error);
      return false;
    }
  }

  // Orders collection
  static async getOrders(): Promise<Order[]> {
    try {
      console.log('🔍 FirestoreService: Getting orders from Firestore...');
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      console.log('📦 FirestoreService: Orders snapshot size:', ordersSnapshot.size);
      console.log('📦 FirestoreService: Orders snapshot empty:', ordersSnapshot.empty);
      
      const orders = ordersSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📦 FirestoreService: Order document:', doc.id, data);
        return {
          ...data,
          id: doc.id
        } as Order;
      });
      
      console.log('✅ FirestoreService: Retrieved orders:', orders);
      return orders;
    } catch (error) {
      console.error('❌ FirestoreService: Error getting orders:', error);
      return [];
    }
  }

  static async getOrdersByCustomer(customerId: string): Promise<Order[]> {
    try {
      const q = query(
        collection(db, 'orders'),
        where('customerId', '==', customerId),
        orderBy('date', 'desc')
      );
      const ordersSnapshot = await getDocs(q);
      return ordersSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Order));
    } catch (error) {
      console.error('Error getting customer orders:', error);
      return [];
    }
  }

  static async addOrder(order: Order): Promise<Order | null> {
    try {
      await setDoc(doc(db, 'orders', order.id), order);
      return order;
    } catch (error) {
      console.error('Error adding order:', error);
      return null;
    }
  }

  static async updateOrder(orderId: string, updates: Partial<Order>): Promise<boolean> {
    try {
      // Clean up undefined values before updating
      const cleanUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      await updateDoc(doc(db, 'orders', orderId), cleanUpdates);
      return true;
    } catch (error) {
      console.error('Error updating order:', error);
      return false;
    }
  }

  static async deleteOrder(orderId: string): Promise<boolean> {
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      return true;
    } catch (error) {
      console.error('Error deleting order:', error);
      return false;
    }
  }

  static async subscribeToOrders(callback: (orders: Order[]) => void): Promise<() => void> {
    try {
      console.log('🔄 FirestoreService: Setting up real-time listener for orders...');
      const unsubscribe = onSnapshot(
        collection(db, 'orders'),
        (snapshot) => {
          console.log('📦 FirestoreService: Real-time update received, orders count:', snapshot.size);
          const orders = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id
          } as Order));
          console.log('📦 FirestoreService: Parsed orders:', orders.length);
          callback(orders);
        },
        (error) => {
          console.error('❌ FirestoreService: Error in orders listener:', error);
        }
      );
      console.log('✅ FirestoreService: Orders listener set up successfully');
      return unsubscribe;
    } catch (error) {
      console.error('❌ FirestoreService: Error setting up orders listener:', error);
      throw error;
    }
  }

  // Tables collection
  static async getTables(): Promise<Table[]> {
    try {
      console.log('🔍 FirestoreService: Getting tables from Firestore...');
      const tablesSnapshot = await getDocs(collection(db, 'tables'));
      console.log('🪑 FirestoreService: Tables snapshot size:', tablesSnapshot.size);
      console.log('🪑 FirestoreService: Tables snapshot empty:', tablesSnapshot.empty);
      
      const tables = tablesSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('🪑 FirestoreService: Table document:', doc.id, data);
        return {
          ...data,
          id: doc.id
        } as Table;
      });
      
      console.log('✅ FirestoreService: Retrieved tables:', tables);
      return tables;
    } catch (error) {
      console.error('❌ FirestoreService: Error getting tables:', error);
      return [];
    }
  }

  static async addTable(table: Omit<Table, 'id'>): Promise<string | null> {
    try {
      console.log('🔥 FirestoreService: Adding table to Firebase:', table);
      const docRef = await addDoc(collection(db, 'tables'), table);
      console.log('✅ FirestoreService: Table added successfully with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ FirestoreService: Error adding table:', error);
      if (error instanceof Error) {
        console.error('❌ FirestoreService: Error message:', error.message);
        console.error('❌ FirestoreService: Error stack:', error.stack);
      }
      return null;
    }
  }

  static async updateTable(tableId: string, updates: Partial<Table>): Promise<boolean> {
    try {
      // Clean up undefined values before updating
      const cleanUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      await updateDoc(doc(db, 'tables', tableId), cleanUpdates);
      return true;
    } catch (error) {
      console.error('Error updating table:', error);
      return false;
    }
  }

  static async deleteTable(tableId: string): Promise<boolean> {
    try {
      console.log('🗑️ FirestoreService: Deleting table with ID:', tableId);
      await deleteDoc(doc(db, 'tables', tableId));
      console.log('✅ FirestoreService: Successfully deleted table:', tableId);
      return true;
    } catch (error) {
      console.error('❌ FirestoreService: Error deleting table:', error);
      console.error('❌ FirestoreService: Error details:', {
        tableId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      return false;
    }
  }

  // Dashboard metrics
  static async getDashboardMetrics(): Promise<DashboardMetrics> {
    try {
      const [ordersSnapshot, customersSnapshot] = await Promise.all([
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'customers'))
      ]);

      const orders = ordersSnapshot.docs.map(doc => doc.data() as Order);
      const customers = customersSnapshot.docs.map(doc => doc.data() as Customer);

      const totalOrders = orders.length;
      const revenue = orders.reduce((sum, order) => sum + order.amount, 0);
      const activeUsers = customers.length;

      // Calculate growth (mock data for now)
      const orderGrowth = 15; // This would be calculated based on historical data
      const revenueGrowth = 12;
      const userGrowth = 8;

      return {
        totalOrders,
        revenue,
        activeUsers,
        orderGrowth,
        revenueGrowth,
        userGrowth
      };
    } catch (error) {
      console.error('Error getting dashboard metrics:', error);
      return {
        totalOrders: 0,
        revenue: 0,
        activeUsers: 0,
        orderGrowth: 0,
        revenueGrowth: 0,
        userGrowth: 0
      };
    }
  }

  // Customer Billing History collection
  static async addCustomerBillingRecord(billingRecord: {
    customerId: string;
    customerName: string;
    customerEmail?: string | null;
    tableId?: string;
    tableNumber?: string;
    tableLocation?: string;
    totalAmount: number;
    paymentMethod: string;
    transactionId?: string | null;
    orderIds: string[];
    numberOfOrders: number;
    totalItems: number;
    paidAt: string;
    sessionStartTime?: string;
    sessionEndTime?: string;
    sessionDurationMinutes?: number | null;
    orderDetails?: any[];
    subtotal?: number;
    gst?: number;
    serviceCharge?: number;
    tableCleanedAt?: string;
    billingStatus?: string;
    createdAt?: string;
    userAgent?: string;
    sessionType?: string;
    [key: string]: any; // Allow additional fields
  }): Promise<string | null> {
    try {
      console.log('💾 FirestoreService: Adding customer billing record:', billingRecord);
      const docRef = await addDoc(collection(db, 'customerBillingHistory'), billingRecord);
      console.log('✅ FirestoreService: Billing record added successfully with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ FirestoreService: Error adding billing record:', error);
      return null;
    }
  }

  static async getCustomerBillingHistory(): Promise<any[]> {
    try {
      console.log('🔍 FirestoreService: Getting customer billing history from Firestore...');
      const billingSnapshot = await getDocs(
        query(collection(db, 'customerBillingHistory'), orderBy('paidAt', 'desc'))
      );
      console.log('💰 FirestoreService: Billing history snapshot size:', billingSnapshot.size);
      
      const billingHistory = billingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('✅ FirestoreService: Retrieved billing history:', billingHistory);
      return billingHistory;
    } catch (error) {
      console.error('❌ FirestoreService: Error getting billing history:', error);
      return [];
    }
  }

  static async getCustomerBillingHistoryByCustomer(customerId: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, 'customerBillingHistory'),
        where('customerId', '==', customerId),
        orderBy('paidAt', 'desc')
      );
      const billingSnapshot = await getDocs(q);
      return billingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting customer billing history:', error);
      return [];
    }
  }

  // Real-time listeners
  static subscribeToCollection(
    collectionName: string,
    callback: (data: any[]) => void,
    constraints: QueryConstraint[] = []
  ) {
    const q = query(collection(db, collectionName), ...constraints);
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(data);
    });
  }

  static subscribeToDocument(
    collectionName: string,
    docId: string,
    callback: (data: any) => void
  ) {
    return onSnapshot(doc(db, collectionName, docId), (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    });
  }
}
